import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const STAGES = [
  {
    num: 'I',
    title: 'Tannery',
    desc: 'Full-grain Italian hide, vegetable-tanned over six weeks until the colour reaches depth.',
  },
  {
    num: 'II',
    title: 'Cut',
    desc: 'Each panel marked and hand-cut against a steel rule. No laser, no die.',
  },
  {
    num: 'III',
    title: 'Stitch',
    desc: 'Saddle-stitched with waxed linen thread, pulled in both directions. No machine can replicate this.',
  },
  {
    num: 'IV',
    title: 'Burnish',
    desc: 'All edges bevelled with a bone folder, burnished to a sealed cord with beeswax.',
  },
  {
    num: 'V',
    title: 'Inspect',
    desc: 'Every piece signed off individually, by hand, before it is wrapped and dispatched.',
  },
];

const INTERVAL_MS = 3000;

export default function CraftStrip() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(
      () => setActive((a) => (a + 1) % STAGES.length),
      INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div
      className="hairline-t hairline-b border-subtle"
      data-testid="craft-strip"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-5">
          {STAGES.map((stage, i) => (
            <button
              key={stage.num}
              className={`relative text-left py-5 px-3 md:px-5 transition-colors duration-300 border-l border-subtle/40 first:border-l-0 focus:outline-none ${
                active === i ? 'bg-mb-surface/30' : 'hover:bg-mb-surface/20'
              }`}
              onClick={() => { setActive(i); setPaused(true); }}
              data-testid={`craft-stage-${i}`}
            >
              {/* Gold progress bar — only on active stage, remounts on each activation */}
              {active === i && (
                <motion.div
                  key={`progress-${i}-${active}`}
                  className="absolute top-0 left-0 right-0 origin-left"
                  style={{ height: '1px', background: 'var(--mb-gold)' }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: INTERVAL_MS / 1000, ease: 'linear' }}
                />
              )}

              {/* Roman numeral */}
              <div
                className="mb-1.5 font-sans"
                style={{
                  fontSize: '8px',
                  letterSpacing: '0.16em',
                  color: active === i ? 'var(--mb-gold)' : 'var(--mb-text-muted)',
                  transition: 'color 300ms',
                }}
              >
                {stage.num}
              </div>

              {/* Stage title */}
              <div
                className="font-display mb-1"
                style={{
                  fontSize: '11px',
                  color: active === i ? 'var(--mb-text)' : 'var(--mb-text-muted)',
                  transition: 'color 300ms',
                  letterSpacing: '0.02em',
                }}
              >
                {stage.title}
              </div>

              {/* Description — fades in when active, desktop only */}
              <div className="hidden md:block" style={{ minHeight: '2.8em' }}>
                <AnimatePresence mode="wait">
                  {active === i && (
                    <motion.p
                      key={`desc-${i}`}
                      className="font-sans leading-relaxed"
                      style={{ fontSize: '10px', color: 'var(--mb-text-muted)', opacity: 0.75 }}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {stage.desc}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
